// react
import { format, set, startOfMonth } from "date-fns";
// dependencies
import moment from "moment";
import React, { KeyboardEvent, useMemo, useRef, useState } from "react";

import { faCalendarDays } from "@fortawesome/pro-regular-svg-icons";

import { InputWrapper, TitleLabel } from "@performyard/py-components";

// components
import Calendar, { CALENDAR_MAX_WIDTH, CALENDAR_MIN_WIDTH } from "./Calendar";
// config
import Config from "./Config";

//hooks
import { useInputId } from "../../../hooks/useInputId";
import { useActiveMonth, useTextInputDate } from "./utils/hooks";

// utils
import coerceToDateObject from "./utils/coerceToDateObject";
import guessDate from "./utils/guessDate";
import isValidDateString from "./utils/isValidDateString";

import { TextSize } from "../../Text";
// components
import InputText from "../InputText";
// styles
import css from "./DatePicker.module.scss";

interface Info {
	title?: string;
	message: string;
	titleBottomLine?: boolean;
}

/**
 * Date picker input component.
 */
interface BaseProps {
	/** The value of the date picker */
	value: string | Date;
	/** The name of the date picker */
	name?: string;
	/** The height of the date picker */
	height?: number;
	/** The default time of the date picker */
	defaultTime?: number;
	/** Does the date picker have a time picker */
	hasTime?: boolean;
	/** Is the date picker disabled */
	disabled?: boolean;
	/** The onChange function */
	onChange: (date?: Date | null) => void;
	/** The placeholder text */
	placeholder?: string;
	/** The date engine type, can handle vanilla JS Date or moment */
	dateEngine?: "vanilla" | "moment";
	/** Does the date picker have an icon */
	hasIcon?: boolean;
	/** Is the icon clearable */
	clearable?: boolean;
	/**
	 * The id of the input. If not provided, a random id will be generated
	 * by nanoid.
	 * @default undefined
	 */
	inputId?: string;
	label?: string;
	labelSize?: TextSize;
	/**
	 * The weight of the label
	 */
	labelWeight?: "regular" | "semi-bold" | "bold";
	errorMessage?: string;
	/**
	 * The image renderd to the right of the title
	 */
	titleImage?: string;
	/**
	 * href of the title
	 */
	titleHref?: string;
	/**
	 * title tooltip
	 */
	titleTooltip?: string;
	textInputClassName?: string;
	/**
	 * Additional information to display when hovering over info icon
	 */
	info?: Info;
}

/** @deprecated Use `DatePicker` from `@performyard/py-components` instead. */
export const DatePicker = ({
	value = "",
	name = "",
	height = 32,
	defaultTime = 12,
	hasTime = false,
	disabled = false,
	onChange = () => {},
	placeholder = "MM/DD/YYYY",
	dateEngine = "vanilla",
	hasIcon = false,
	clearable = false,
	label = "",
	errorMessage = "",
	inputId = undefined,
	titleImage,
	titleHref,
	textInputClassName,
	info = undefined,
	titleTooltip,
	labelSize,
	labelWeight,
}: BaseProps) => {
	const [inputValue, setInputValue] = useTextInputDate(value, Config.displayFormat(hasTime));
	const [hasFocus, setHasFocus] = useState(false);
	const [activeMonth, setActiveMonth] = useActiveMonth(value);
	const [ignoreBlur, setIgnoreBlur] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const inputIdentifier = useInputId(inputId);
	const datePickerRef = useRef<HTMLDivElement>(null);

	// sets width of calendar based on width of Calendar component's parent container
	// limits width to a range of 184px to 475px
	const getCalendarWidth = () => {
		if (datePickerRef.current) {
			if (datePickerRef.current.offsetWidth > CALENDAR_MAX_WIDTH) {
				return CALENDAR_MAX_WIDTH;
			}
			return datePickerRef.current.offsetWidth - 22; // remove 22px for calendar border + padding
		}
		return CALENDAR_MIN_WIDTH;
	};

	const setFocus = (focusState: boolean) => {
		let nextFocusState = focusState;

		if (nextFocusState === false && ignoreBlur) {
			return;
		}

		if (focusState === undefined) {
			nextFocusState = !hasFocus;
		} else if (disabled) {
			nextFocusState = false;
		}

		setHasFocus(nextFocusState);
		if (nextFocusState) {
			setIgnoreBlur(false);
		}
	};

	// triggers the onChange prop, handles the date engine type
	const triggerOnChange = (newValue) => {
		// if date engine is moment, convert value to moment object
		const isMoment = dateEngine === "moment" && moment(newValue).isValid();
		const coercedValue = isMoment ? moment(newValue, "YYYY.MM.DD") : newValue;
		onChange(coercedValue);
	};

	const handleSelectDate = (newValue) => {
		if (newValue === null || newValue === "") {
			onChange(null);
			setInputValue("");
		} else {
			triggerOnChange(newValue);
			setHasFocus(false);
			setInputValue(format(newValue, Config.displayFormat(hasTime)));
		}
		setIgnoreBlur(false);
	};

	const addTimeToDate = (date: Date) => {
		// we get the selected time from the input value
		const dateInput = new Date(inputValue);
		// allow time input if time option exists in config
		const timeOptionsTimes = Config.timeOptions.map((time) => time.value);
		const timeSetDate = set(date, {
			hours: timeOptionsTimes.includes(dateInput.getHours()) ? dateInput.getHours() : defaultTime,
			minutes: 0,
			seconds: 0,
			milliseconds: 0,
		});
		return timeSetDate;
	};

	//handles the blur event which is triggered when the input loses focus
	const handleBlur = () => {
		if (ignoreBlur) {
			return;
		}

		const value = inputValue;
		const validDate = isValidDateString(value);

		if (validDate.isValid && validDate.date) {
			handleCoercedUpdate();
		} else {
			setInputValue("");
			onChange(null);
			setHasFocus(false);
		}
		setFocus(false);
	};

	const handleInputChange = (newValue: string) => {
		const bestGuess = guessDate(newValue, true);

		// This checks to see if the date text input is empty,
		// If empty we want to save it as an empty string. ''
		// Without this we lose the ability to delete inputs with dates.
		if (newValue === "") {
			onChange(null);
			setHasFocus(false);
		}
		bestGuess && setActiveMonth(startOfMonth(bestGuess));
		setInputValue(newValue);
	};

	const handleCoercedUpdate = () => {
		const bestGuess = guessDate(inputValue, false);
		const validDate = isValidDateString(inputValue);
		if (bestGuess && hasTime && validDate) {
			const timeSetBestGuess = addTimeToDate(bestGuess);
			triggerOnChange(timeSetBestGuess);
			handleInputChange(format(timeSetBestGuess, Config.displayFormat(hasTime)));
		} else if (bestGuess) {
			handleInputChange(format(bestGuess, Config.displayFormat(hasTime)));
			triggerOnChange(bestGuess);
		}
	};

	const handleKeyDown = ({ key: keyValue }: KeyboardEvent) => {
		if (["Enter"].includes(keyValue)) {
			handleCoercedUpdate();
			setHasFocus(false);
		}
	};

	const dateValue = useMemo(() => {
		return coerceToDateObject(value);
	}, [value]);

	const parentStyle = {
		height: height,
	};

	const iconClass = useMemo(() => {
		if (!hasIcon) return "";
		if (hasFocus) {
			return `${css.calendarIcon} ${css.focused} ${disabled ? css.disabledIcon : ""}`;
		} else {
			return `${css.calendarIcon} ${disabled ? css.disabledIcon : ""}`;
		}
	}, [hasFocus, hasIcon]);

	const labelNode =
		label && (titleHref || titleImage) ? (
			<TitleLabel label={label} titleHref={titleHref} titleImage={titleImage} titleTooltip={titleTooltip} />
		) : (
			label
		);

	return (
		<InputWrapper
			label={labelNode}
			info={info}
			id={inputIdentifier}
			labelSize={labelSize}
			labelWeight={labelWeight}
		>
			<div
				ref={datePickerRef}
				data-testid="date-picker"
				className={`${css.cpDatepicker} cp-datepicker`}
				style={label ? undefined : parentStyle}
			>
				<InputText
					ref={inputRef}
					disabled={disabled}
					name={name}
					value={inputValue}
					onChange={handleInputChange}
					onKeyDown={handleKeyDown}
					onFocus={() => {
						if (!hasFocus && !disabled) {
							setHasFocus(true);
						}
					}}
					onBlur={handleBlur}
					onClickIcon={() => {
						const closed = !hasFocus && !disabled;
						setHasFocus(closed);
						if (closed) {
							inputRef?.current?.focus();
							setIgnoreBlur(true);
						}
					}}
					style={{ height }}
					placeholder={placeholder}
					icon={hasIcon ? faCalendarDays : undefined}
					iconPlacement="right"
					iconClass={iconClass}
					clearIconClass={css.clearIcon}
					clearable={clearable}
					inputId={inputIdentifier}
					errorMessage={errorMessage}
					className={textInputClassName}
				/>

				<div
					style={{ zIndex: 2 }}
					onMouseLeave={() => setIgnoreBlur(false)}
					onMouseEnter={() => setIgnoreBlur(true)}
				>
					{hasFocus ? (
						<Calendar
							value={dateValue}
							hasTime={hasTime}
							defaultTime={defaultTime}
							setDate={handleSelectDate}
							changeActiveMonth={setActiveMonth}
							activeMonth={activeMonth}
							width={getCalendarWidth()}
						/>
					) : (
						""
					)}
				</div>
			</div>
		</InputWrapper>
	);
};

/** @deprecated Use `DatePicker` from `@performyard/py-components` instead. */
export default DatePicker;
